/**
 * AB#22185 (Zendesk #9938) — "Last updated by Name · date" on an order's files:
 * the tech pack, each merchant tech pack PDF version, the production files of
 * each order item and the custom tech pack PDF. The backend reads the latest
 * order activity log entry for each (GET orders/{id}/file-stamps).
 *
 * One request per order, shared by every component that shows a stamp (the
 * action bar and the products tab). A caller without access to the activity log
 * gets a 403: no stamps, and no toast — the stamp is a hint, never an error.
 */
import { computed, reactive, watch, type ComputedRef, type Ref } from 'vue';

import { http } from '@/interceptors/axiosRequestInterceptor';

export interface FileStamp {
  by: string;
  user_id: number | null;
  at: string | null;
  action_type: string;
  action?: 'uploaded' | 'removed';
}

export interface OrderFileStampsData {
  techpack: FileStamp | null;
  /** Keyed as the order's merchant_techpack_pdf_urls: 0 is the current PDF. */
  techpack_versions: Record<string, FileStamp>;
  /** Keyed by order item id. */
  production_files: Record<string, FileStamp>;
  custom_pdf: FileStamp | null;
}

const EMPTY: OrderFileStampsData = {
  techpack: null,
  techpack_versions: {},
  production_files: {},
  custom_pdf: null,
};

const store = reactive<Record<number, OrderFileStampsData>>({});
const inFlight = new Map<number, Promise<void>>();

const asRecord = (v: unknown): Record<string, FileStamp> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, FileStamp>) : {};

/**
 * Fetch (or re-fetch) the stamps of one order into the shared store. Callers
 * asking at the same moment share one request.
 */
export function loadOrderFileStamps(orderId: number, force = false): Promise<void> {
  const pending = inFlight.get(orderId);
  if (pending) return pending;
  if (!force && store[orderId]) return Promise.resolve();
  const p = Promise.resolve()
    .then(() => http.get(`orders/${orderId}/file-stamps`))
    .then((res) => {
      const data = res?.data;
      const r = (data?.result ?? {}) as Partial<OrderFileStampsData>;
      store[orderId] = {
        techpack: r.techpack ?? null,
        techpack_versions: asRecord(r.techpack_versions),
        production_files: asRecord(r.production_files),
        custom_pdf: r.custom_pdf ?? null,
      };
    })
    .catch(() => {
      store[orderId] = { ...EMPTY };
    })
    .finally(() => inFlight.delete(orderId));
  inFlight.set(orderId, p);
  return p;
}

/** Test seam. */
export function resetOrderFileStamps(): void {
  for (const k of Object.keys(store)) delete store[Number(k)];
  inFlight.clear();
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useOrderFileStamps(orderId: Ref<number | null | undefined>) {
  watch(
    orderId,
    (id) => {
      // Fresh on every open: the tech pack may have been saved on another page.
      if (id) void loadOrderFileStamps(id, true);
    },
    { immediate: true }
  );

  const stamps: ComputedRef<OrderFileStampsData> = computed(() =>
    orderId.value ? (store[orderId.value] ?? EMPTY) : EMPTY
  );

  const refresh = (): Promise<void> =>
    orderId.value ? loadOrderFileStamps(orderId.value, true) : Promise.resolve();

  return { stamps, refresh };
}

const dateOnly = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const dateTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const parse = (at: string | null): Date | null => {
  if (!at) return null;
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "Sara Lund · 12 Oct 2026" */
export function stampLabel(s: FileStamp | null | undefined): string {
  if (!s) return '';
  const d = parse(s.at);
  return d ? `${s.by} · ${dateOnly.format(d)}` : s.by;
}

/** "Tech pack last updated by Sara Lund, 12 Oct 2026, 14:05" — for a tooltip. */
export function stampTitle(what: string, s: FileStamp | null | undefined): string {
  if (!s) return '';
  const d = parse(s.at);
  const verb = s.action === 'removed' ? 'removed' : 'last updated';
  return `${what} ${verb} by ${s.by}${d ? `, ${dateTime.format(d)}` : ''}`;
}
